import React from 'react';
import { Card, Chip, Stack, Typography } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import type { PassTargetDetail } from '../../models/Pass';
import { passMetaChipSx } from './passDetailLayout';

export interface PassSpaceTargetCardProps {
  target: Pick<PassTargetDetail, 'name' | 'description' | 'city'>;
  onClick?: () => void;
}

/** Room target row styled like `TutorLessonOptionCard` for pass wallet/detail surfaces. */
const PassSpaceTargetCard: React.FC<PassSpaceTargetCardProps> = ({ target, onClick }) => {
  const clickable = typeof onClick === 'function';
  const city = target.city?.trim() || null;
  const name = target.name?.trim() || '';
  const rawDescription = target.description?.trim() || null;
  // Many spaces store the name as description; don't repeat it under the title.
  const description =
    rawDescription && rawDescription.toLocaleLowerCase() !== name.toLocaleLowerCase()
      ? rawDescription
      : null;

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        borderColor: 'divider',
        bgcolor: 'background.default',
        cursor: clickable ? 'pointer' : 'default',
        transition: clickable ? 'all 0.2s ease' : undefined,
        ...(clickable
          ? {
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: (theme) => theme.shadows[2],
              },
            }
          : {}),
      }}
    >
      <Stack spacing={0.75}>
        <Stack spacing={0.75} minWidth={0}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {name || target.name}
          </Typography>
          {city ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<PlaceOutlinedIcon aria-hidden />}
              label={city}
              sx={passMetaChipSx}
            />
          ) : null}
        </Stack>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
      </Stack>
    </Card>
  );
};

export default PassSpaceTargetCard;
